import React, { useEffect, useState, lazy, Suspense } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { fetchBlogDetail } from "../api/blogApi";
import moment from "moment";
import { FiX } from "react-icons/fi";
import Blogbg from "../assets/images/single-blog-bg.jpg";
import gamerjiLogo from "../assets/images/logog.png";
import { useTranslation } from "react-i18next";
import { Blog } from "@gtypes/blog"; // ✅ Use alias

// Lazy imports
const Header = lazy(() => import("../common/header"));
const Footer = lazy(() => import("../common/Footer"));
const Subbanner = lazy(() => import("../common/Subbanner"));
const SidebarBlogList = lazy(() => import("./SidebarBlogList"));
const RoundLoader = lazy(() => import("../common/RoundLoader"));

const BlogDetail: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const blogIdFromState = (location.state as { blogId?: string })?.blogId;
  const blogId = blogIdFromState || sessionStorage.getItem("blogId");

  const [blog, setBlog] = useState<Blog | null>(null); // ✅ Use Blog type
  const [loading, setLoading] = useState<boolean>(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(false);

  const image_Url = "https://storage.googleapis.com/gamerji-uploads/";

  useEffect(() => {
    if (blogIdFromState) {
      sessionStorage.setItem("blogId", blogIdFromState);
    }
  }, [blogIdFromState]);

  useEffect(() => {
    if (!blogId) navigate("/");
  }, [blogId, navigate]);

  useEffect(() => {
    const loadDetail = async () => {
      setLoading(true);
      try {
        const result = await fetchBlogDetail(blogId as string);
        setBlog(result);
      } catch (error) {
        console.error("Error loading blog detail:", error);
      } finally {
        setLoading(false);
      }
    };
    if (blogId) loadDetail();
  }, [blogId]);

  const imageSrc = blog?.img?.default ? `${image_Url}${blog.img.default}` : "";

  useEffect(() => {
    if (blogIdFromState) {
      sessionStorage.setItem("blogId", blogIdFromState);
    }
  }, [blogIdFromState]);

  useEffect(() => {
    if (!blogId) navigate("/");
  }, [blogId, navigate]);

  useEffect(() => {
    const loadDetail = async () => {
      setLoading(true);
      try {
        const result = await fetchBlogDetail(blogId as string);
        setBlog(result);
      } catch (error) {
        console.error("Error loading blog detail:", error);
      } finally {
        setLoading(false);
      }
    };
    if (blogId) loadDetail();
  }, [blogId]);

  if (loading || !blog) {
    return (
      <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-white">Loading...</div>}>
        <RoundLoader size={8} color="#f92c2c" borderWidth={5} />
      </Suspense>
    );
  }

  return (
    <div className="w-full h-full bg-white relative">
      <Suspense fallback={<div className="h-[60px] bg-[#070B28] w-full" />}>
        <Header />
      </Suspense>

      <Suspense fallback={<div className="h-[30vh] bg-gray-200 w-full" />}>
        <Subbanner height="30vh" />
      </Suspense>

      <div className="block lg:hidden text-right pr-4">
        <button onClick={() => setIsMobileMenuOpen(true)} className="text-2xl text-[#f92c2c]">
          {t("blog.more")}
        </button>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-16 grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <div className="relative rounded-xl overflow-hidden shadow-lg h-[300px] bg-[rgb(255,198,9)]">
            <img
              src={Blogbg}
              alt="Blog Background"
              className="absolute inset-0 w-full h-full object-cover opacity-40 z-0"
            />
            {imageSrc ? (
              <img
                src={imageSrc}
                alt={blog.title}
                className="absolute inset-0 w-full h-full object-cover z-10"
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center text-gray-700 text-lg font-semibold z-10">
                {t("blog.noImage")}
              </div>
            )}
            {/* <div className="absolute bottom-0 left-0 right-0 p-4 text-white flex items-center justify-between z-20">
              <div className="flex items-center gap-2">
                <img
                  src={gamerjiLogo}
                  alt="Gamerji"
                  className="w-10 h-10 rounded-full bg-white border-2 border-white"
                />
                <div>
                  <p className="text-sm">{moment(blog.createdAt).format("ll")}</p>
                  <p className="font-bold">Gamerji</p>
                </div>
              </div>
            </div> */}
          </div>

          <div className="mt-8 text-left">
            <h1 className="text-3xl font-bold text-[#070b28] mb-4">{blog.title}</h1>
            <div
              className="prose max-w-none prose-p:text-[#333] prose-p:leading-relaxed prose-a:text-blue-600"
              dangerouslySetInnerHTML={{ __html: blog.description }}
            />
          </div>
        </div>

        {/* Desktop Sidebar */}
        <div className="hidden lg:block">
          <Suspense fallback={<div className="h-96 bg-gray-100" />}>
            <SidebarBlogList />
          </Suspense>
        </div>
      </div>

      {/* Mobile Drawer */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-end">
          <div className="w-[85%] max-w-sm bg-white shadow-xl h-full overflow-y-auto">
            <div className="flex justify-end p-4 border-b">
              <button onClick={() => setIsMobileMenuOpen(false)} className="text-xl">
                <FiX />
              </button>
            </div>
            <Suspense fallback={<div className="p-4">Loading...</div>}>
              <SidebarBlogList />
            </Suspense>
          </div>
        </div>
      )}

      <Suspense fallback={<div className="h-20 bg-[#070B28]" />}>
        <Footer />
      </Suspense>
    </div>
  );
};

export default BlogDetail;
